import { useState } from 'react';
import { ScrollReveal } from './ScrollReveal';
import { useLang } from '@/lib/translations';

const INDUSTRIES = [
  { id: 'pe', label: 'Private Equity', challenge: 'Complex portfolio oversight, multi-entity coordination, and structured reporting across holdings.',
    solution: 'Deploy agents for portfolio monitoring, deal coordination, research support, and structured executive reporting across entities.',
    why: 'A private multi-agent architecture enables controlled access per entity while maintaining centralized visibility.' },
  { id: 'fo', label: 'Family Offices', challenge: 'Coordination across documents, vendors, travel, correspondence, priorities, approvals, and reporting.',
    solution: 'Private Office and Executive agents manage daily operations, vendor workflows, and priority coordination.',
    why: 'Family offices require structured privacy and discretion — agents operate within controlled boundaries.' },
  { id: 'law', label: 'Law Firms', challenge: 'High-volume document review, deadline management, internal knowledge retrieval, and matter support.',
    solution: 'Legal agents screen contracts, summarize clauses, track deadlines, and support due diligence workflows.',
    why: 'Approval-based agent actions ensure sensitive matters receive proper human oversight.' },
  { id: 'health', label: 'Healthcare Groups', challenge: 'Process support, documentation flows, internal coordination, and operational structure across facilities.',
    solution: 'Operations and compliance agents streamline documentation, internal coordination, and structured process support.',
    why: 'Healthcare requires strict policy control and audit trails — built into every agent layer.' },
  { id: 'finance', label: 'Financial Institutions', challenge: 'Research support, structured internal workflows, controlled access, and multi-team coordination.',
    solution: 'Research, finance, and compliance agents support reporting, analysis, and structured internal workflows.',
    why: 'Role-based permissions and separation ensure regulatory and operational requirements are respected.' },
  { id: 'realestate', label: 'Real Estate', challenge: 'Deal tracking, follow-up, project coordination, document structure, and executive summaries.',
    solution: 'Sales and operations agents manage deal pipelines, coordinate follow-ups, and generate structured summaries.',
    why: 'Multi-project coordination benefits from orchestrated agents that maintain context across deals.' },
  { id: 'luxury', label: 'Luxury Brands', challenge: 'Brand consistency, client operations, internal support, and multi-market coordination.',
    solution: 'Marketing, sales, and support agents maintain brand consistency while coordinating across markets.',
    why: 'Premium brands require discreet, controlled agent behavior aligned with brand standards.' },
  { id: 'trading', label: 'Trading Companies', challenge: 'Supply chain coordination, vendor management, cross-border logistics, and operational complexity.',
    solution: 'Procurement and operations agents track supplier workflows, coordinate logistics, and structure internal overviews.',
    why: 'Multi-country operations benefit from agents that work within structured permission hierarchies.' },
  { id: 'executive', label: 'Executive Offices', challenge: 'Strategic coordination, briefing preparation, decision support, and cross-functional visibility.',
    solution: 'Executive agents prepare briefings, track priorities, and provide structured decision support.',
    why: 'Executive environments require premium control and visibility over all agent activities.' },
  { id: 'multi', label: 'Multi-Entity Businesses', challenge: 'Cross-entity coordination, separate governance, unified reporting, and scalable operations.',
    solution: 'Full multi-agent deployment with entity separation, unified orchestration, and executive oversight.',
    why: 'The architecture supports entity-level separation with central visibility — ideal for complex organizations.' },
];

export default function Industries() {
  const [active, setActive] = useState(0);
  const { t } = useLang();
  const tr = t.industries;
  const industry = INDUSTRIES[active];

  return (
    <section id="industries" className="relative py-24 sm:py-32" data-testid="industries-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <ScrollReveal>
          <div className="overline mb-4">{tr.overline}</div>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-aurocle-text mb-4">{tr.title}</h2>
          <p className="text-base text-aurocle-platinum-muted max-w-2xl mb-12">{tr.sub}</p>
        </ScrollReveal>

        <ScrollReveal delay={100}>
          <div className="flex flex-wrap gap-2 mb-10">
            {INDUSTRIES.map((ind, i) => (
              <button
                key={ind.id}
                onClick={() => setActive(i)}
                className={`px-4 py-2 text-xs font-mono uppercase tracking-wider transition-all border ${
                  active === i
                    ? 'bg-aurocle-gold/10 border-aurocle-gold/30 text-aurocle-gold'
                    : 'border-white/8 text-aurocle-text-secondary hover:border-white/15 hover:text-aurocle-text'
                }`}
                data-testid={`industry-tab-${ind.id}`}
              >
                {tr.tabs[i] || INDUSTRIES[i].label}
              </button>
            ))}
          </div>
        </ScrollReveal>

        <ScrollReveal delay={200}>
          <div className="grid lg:grid-cols-3 gap-6" data-testid="industry-content">
            <div className="glass-card p-8">
              <div className="text-xs font-mono uppercase tracking-wider text-red-400/60 mb-3">{tr.challengeLabel}</div>
              <p className="text-sm text-aurocle-text leading-relaxed">{industry.challenge}</p>
            </div>
            <div className="glass-card p-8 border-aurocle-gold/10">
              <div className="text-xs font-mono uppercase tracking-wider text-aurocle-gold/60 mb-3">{tr.solutionLabel}</div>
              <p className="text-sm text-aurocle-text leading-relaxed">{industry.solution}</p>
            </div>
            <div className="glass-card p-8">
              <div className="text-xs font-mono uppercase tracking-wider text-aurocle-platinum-muted/60 mb-3">{tr.whyLabel}</div>
              <p className="text-sm text-aurocle-text leading-relaxed">{industry.why}</p>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
